import { DateRange, FiltersDefinition, FiltersState } from '@factorialco/f0-react/dist/experimental'
import { AnalyticsReportResultsQuery } from 'generated/resources/graphql'

type Schema =
  AnalyticsReportResultsQuery['iris']['sqlResultsConnection']['nodes'][number]['schema'][number]

function filterForType(column: Schema): FiltersDefinition<string> {
  const label = column.displayName ?? column.name
  if (column.labeledEnumValues.length > 0) {
    return {
      [column.name]: {
        type: 'in',
        label,
        options: {
          options: column.labeledEnumValues,
          cache: true,
        },
      },
    }
  }

  if (column.dataType === 'string' && column.formatHint === 'date') {
    return { [column.name]: { type: 'date', options: { mode: 'range' }, label } }
  }

  if (column.dataType === 'string') {
    return { [column.name]: { type: 'search', label } }
  }

  if (column.dataType === 'number') {
    return { [column.name]: { type: 'number', label } }
  }

  return {}
}

export function filterDefinitionsFromSchema(schema: Schema[]): FiltersDefinition<string> {
  return schema.map(filterForType).reduce((acc, curr) => ({ ...acc, ...curr }), {})
}

type JsonLogicOperator =
  | 'var'
  | 'missing'
  | 'missing_some'
  | 'if'
  | '=='
  | '==='
  | '!='
  | '!=='
  | '!'
  | '!!'
  | 'or'
  | 'and'
  | '>'
  | '>='
  | '<'
  | '<='
  | 'between'
  | 'max'
  | 'min'
  | '+'
  | '-'
  | '*'
  | '/'
  | '%'
  | 'map'
  | 'filter'
  | 'reduce'
  | 'all'
  | 'none'
  | 'some'
  | 'merge'
  | 'in'
  | 'cat'
  | 'substr'
  | 'log'

type JsonLogicFilter = {
  [K in JsonLogicOperator]?: JsonLogicFilter | unknown | Array<JsonLogicFilter | unknown>
}

type VarExpression = {
  var: string
}

type Literal = number | string | boolean | Date

type InExpressionEnum = {
  in: [VarExpression, Array<unknown>]
}

type InExpressionSearch = {
  in: [string, VarExpression]
}

type GreaterThanExpression = {
  '>': [VarExpression | Literal, VarExpression | Literal]
}

type LessThanExpression = {
  '<': [VarExpression | Literal, VarExpression | Literal]
}

type GreaterThanOrEqualExpression = {
  '>=': [VarExpression | Literal, VarExpression | Literal]
}

type LessThanOrEqualExpression = {
  '<=': [VarExpression | Literal, VarExpression | Literal]
}

type EqualityExpression = {
  '==': [VarExpression | Literal, VarExpression | Literal]
}

type StrictEqualityExpression = {
  '===': [VarExpression | Literal, VarExpression | Literal]
}

type BetweenExpression = {
  between: [Literal, VarExpression, Literal]
}

type Expression =
  | InExpressionEnum
  | InExpressionSearch
  | BetweenExpression
  | GreaterThanOrEqualExpression
  | LessThanOrEqualExpression

type AndExpression = {
  and: Array<Expression>
}

type FilterResult = {
  fieldName: string
  value: unknown
}

type ParseErrorType = 'INVALID_STRUCTURE'

type ParseError = {
  type: ParseErrorType
  message: string
  context?: Record<string, unknown>
}

type ParseResult = { success: true; filter: FilterResult } | { success: false; error: ParseError }

export function datatableFiltersToJsonLogicFilters(
  filters: FiltersState<FiltersDefinition<string>>
): JsonLogicFilter | undefined {
  const conditions = Object.entries(filters)
    .map(([fieldName, filterValue]) => {
      // Skip undefined/null/empty values
      if (filterValue === undefined || filterValue === null || filterValue === '') {
        return null
      }

      // Number filters
      // FIXME: This condition is precarious to say the least. It's meant to
      // catch number ranges but not multiselect filters, which also return
      // arrays... but what if a multiselect filter returns numbers? Then it
      // will interpret as a range and fail...
      if (
        Array.isArray(filterValue) &&
        filterValue.length === 2 &&
        (typeof filterValue[0] === 'number' || typeof filterValue[1] === 'number')
      ) {
        const startValue = filterValue[0]
        const endValue = filterValue[1]
        if (startValue == null || endValue == null) {
          if (endValue == null)
            return {
              '>=': [{ var: fieldName }, startValue],
            }
          if (startValue == null)
            return {
              '<=': [{ var: fieldName }, endValue],
            }
        }
        if (startValue === endValue) {
          return { '===': [{ var: fieldName }, startValue] }
        }

        return { between: [startValue, { var: fieldName }, endValue] }
      }

      // Check for DateRange object: { from: Date, to: Date }
      // NOTE: Order matters here. If date is not checked first, it will be interpreted as an enum filter.
      if (isDateRange(filterValue)) {
        return { between: [filterValue.from, { var: fieldName }, filterValue.to] }
      }

      // Enum filters (arrays)
      if (Array.isArray(filterValue)) {
        if (filterValue.length === 0) return null
        if (filterValue.length === 1) return { '==': [{ var: fieldName }, filterValue[0]] }
        if (filterValue.length > 1) return { in: [{ var: fieldName }, filterValue] }
      }

      // Everything else: treat as string search
      return { in: [String(filterValue), { var: fieldName }] }
    })
    .filter((condition): condition is NonNullable<typeof condition> => condition !== null)

  if (conditions.length === 0) {
    return undefined
  }

  return { and: conditions }
}

// ============================================================================
// Type Guards
// ============================================================================

const isObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null
}

const isVarExpression = (value: unknown): value is VarExpression => {
  return isObject(value) && 'var' in value && typeof value.var === 'string'
}

const isLiteral = (value: unknown): value is Literal => {
  return (
    typeof value === 'number' ||
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    value instanceof Date
  )
}

const isOperatorExpression = (operator: string, value: unknown): boolean => {
  return (
    isObject(value) &&
    operator in value &&
    Array.isArray(value[operator]) &&
    value[operator].length === 2 &&
    (isVarExpression(value[operator][0]) || isLiteral(value[operator][0])) &&
    (isVarExpression(value[operator][1]) || isLiteral(value[operator][1]))
  )
}

const isGreaterThanExpression = (value: unknown): value is GreaterThanExpression => {
  return isOperatorExpression('>', value)
}

const isLessThanExpression = (value: unknown): value is LessThanExpression => {
  return isOperatorExpression('<', value)
}

const isGreaterThanOrEqualExpression = (value: unknown): value is GreaterThanOrEqualExpression => {
  return isOperatorExpression('>=', value)
}

const isLessThanOrEqualExpression = (value: unknown): value is LessThanOrEqualExpression => {
  return isOperatorExpression('<=', value)
}

const isEqualityExpression = (value: unknown): value is EqualityExpression => {
  return isOperatorExpression('==', value)
}

const isStrictEqualityExpression = (value: unknown): value is StrictEqualityExpression => {
  return isOperatorExpression('===', value)
}

const isInExpressionEnum = (value: unknown): value is InExpressionEnum => {
  if (!isObject(value) || !('in' in value) || !Array.isArray(value.in)) {
    return false
  }
  const [first, second] = value.in
  return value.in.length === 2 && isVarExpression(first) && Array.isArray(second)
}

const isInExpressionSearch = (value: unknown): value is InExpressionSearch => {
  if (!isObject(value) || !('in' in value) || !Array.isArray(value.in)) {
    return false
  }
  const [first, second] = value.in

  // Handle both direct VarExpression and array with single VarExpression
  const varExpr = Array.isArray(second) && second.length === 1 ? second[0] : second

  return value.in.length === 2 && typeof first === 'string' && isVarExpression(varExpr)
}

const isBetweenExpression = (value: unknown): value is BetweenExpression => {
  return (
    isObject(value) &&
    'between' in value &&
    Array.isArray(value.between) &&
    value.between.length === 3 &&
    isLiteral(value.between[0]) &&
    isLiteral(value.between[2]) &&
    isVarExpression(value.between[1])
  )
}

const isSuccessResult = (
  result: ParseResult
): result is { success: true; filter: FilterResult } => {
  return result.success
}

const isDateRange = (value: unknown): value is DateRange => {
  return (
    isObject(value) &&
    'from' in value &&
    'to' in value &&
    value.from instanceof Date &&
    value.to instanceof Date
  )
}

const extractAndConditions = (
  parsed: unknown
): { success: true; data: unknown[] } | { success: false; error: string } => {
  if (!isObject(parsed) || !('and' in parsed) || !Array.isArray(parsed.and)) {
    return { success: false, error: 'Missing "and" array in JsonLogic structure' }
  }
  return { success: true, data: parsed.and }
}

// ============================================================================
// Pattern Matchers
// ============================================================================

const parseInExpressionEnum = (expression: InExpressionEnum): ParseResult => {
  const fieldName = expression.in[0].var
  const values = expression.in[1]

  const filteredValues = values.filter((v): v is string | number => v !== '')
  if (filteredValues.length === 0) {
    return {
      success: false,
      error: { type: 'INVALID_STRUCTURE', message: 'No valid values provided' },
    }
  }

  return {
    success: true,
    filter: { fieldName, value: filteredValues },
  }
}

const parseInExpressionSearch = (expression: InExpressionSearch): ParseResult => {
  const searchValue = expression.in[0]
  const secondElement = expression.in[1]

  // Handle both direct VarExpression and array with single VarExpression
  const varExpr =
    Array.isArray(secondElement) && secondElement.length === 1 ? secondElement[0] : secondElement
  const fieldName = varExpr.var

  return {
    success: true,
    filter: { fieldName, value: searchValue },
  }
}

const parseBetweenExpression = (expression: BetweenExpression): ParseResult => {
  const [from, varExpr, to] = expression.between
  const fieldName = varExpr.var

  if (typeof from !== 'string' || typeof to !== 'string') {
    return {
      success: true,
      filter: {
        fieldName,
        value: [from, to],
      },
    }
  }

  try {
    return {
      success: true,
      filter: {
        fieldName,
        value: {
          from: new Date(from),
          to: new Date(to),
        },
      },
    }
  } catch (error) {
    return {
      success: false,
      error: {
        type: 'INVALID_STRUCTURE',
        message: `Error parsing dates for column ${fieldName}`,
        context: { fieldName, from, to, error: String(error) },
      },
    }
  }
}

const parseLessThanExpression = (expression: LessThanExpression): ParseResult => {
  const [first, second] = expression['<']

  if (isVarExpression(first) && !isVarExpression(second)) {
    // z < 10
    return {
      success: false,
      error: {
        type: 'INVALID_STRUCTURE',
        message: 'Variable must be on the right side of the operator',
      },
    }
  }
  if (isVarExpression(second) && !isVarExpression(first)) {
    // 10 < z (can be rewritten as z >= 10)
    return {
      success: true,
      filter: { fieldName: second.var, value: [first, undefined] },
    }
  } else {
    return {
      success: false,
      error: { type: 'INVALID_STRUCTURE', message: 'Exactly one operand must be a variable' },
    }
  }
}

const parseGreaterThanExpression = (expression: GreaterThanExpression): ParseResult => {
  const [first, second] = expression['>']

  if (isVarExpression(first) && !isVarExpression(second)) {
    // z > 10
    return {
      success: false,
      error: {
        type: 'INVALID_STRUCTURE',
        message: 'Variable must be on the right side of the operator',
      },
    }
  }
  if (isVarExpression(second) && !isVarExpression(first)) {
    // 10 > z (can be rewritten as z <= 10)
    return {
      success: true,
      filter: { fieldName: second.var, value: [undefined, first] },
    }
  } else {
    return {
      success: false,
      error: { type: 'INVALID_STRUCTURE', message: 'Exactly one operand must be a variable' },
    }
  }
}

const parseGreaterThanOrEqualExpression = (
  expression: GreaterThanOrEqualExpression
): ParseResult => {
  const [first, second] = expression['>=']

  if (isVarExpression(first) && !isVarExpression(second)) {
    // z => 10
    return {
      success: true,
      filter: { fieldName: first.var, value: [second, undefined] },
    }
  }
  if (isVarExpression(second) && !isVarExpression(first)) {
    // 10 => z
    return {
      success: false,
      error: {
        type: 'INVALID_STRUCTURE',
        message: 'Variable must be on the left side of the operator',
      },
    }
  } else {
    return {
      success: false,
      error: { type: 'INVALID_STRUCTURE', message: 'Exactly one operand must be a variable' },
    }
  }
}

const parseLessThanOrEqualExpression = (expression: LessThanOrEqualExpression): ParseResult => {
  const [first, second] = expression['<=']

  if (isVarExpression(first) && !isVarExpression(second)) {
    // z => 10
    return {
      success: true,
      filter: { fieldName: first.var, value: [undefined, second] },
    }
  }
  if (isVarExpression(second) && !isVarExpression(first)) {
    // 10 => z
    return {
      success: false,
      error: {
        type: 'INVALID_STRUCTURE',
        message: 'Variable must be on the left side of the operator',
      },
    }
  } else {
    return {
      success: false,
      error: { type: 'INVALID_STRUCTURE', message: 'Exactly one operand must be a variable' },
    }
  }
}

const parseEqualityExpression = (
  expression: EqualityExpression | StrictEqualityExpression
): ParseResult => {
  const [first, second] = isEqualityExpression(expression) ? expression['=='] : expression['===']

  if (isVarExpression(first) && !isVarExpression(second)) {
    // When filter isn't numeric, the data collection is expecting a select filter, not a range filter.
    if (typeof second !== 'number')
      return { success: true, filter: { fieldName: first.var, value: [second] } }

    // z == 10
    return {
      success: true,
      filter: { fieldName: first.var, value: [second, second] },
    }
  }
  if (isVarExpression(second) && !isVarExpression(first)) {
    // When filter isn't numeric, the data collection is expecting a select filter, not a range filter.
    if (typeof first !== 'number')
      return { success: true, filter: { fieldName: second.var, value: [first] } }

    // 10 == z
    return {
      success: true,
      filter: { fieldName: second.var, value: [first, first] },
    }
  } else {
    return {
      success: false,
      error: { type: 'INVALID_STRUCTURE', message: 'Exactly one operand must be a variable' },
    }
  }
}

const parseCondition = (condition: unknown): ParseResult => {
  if (!isObject(condition)) {
    return {
      success: false,
      error: { type: 'INVALID_STRUCTURE', message: 'Condition is not an object' },
    }
  }

  // Try parsing as InExpression (enum pattern)
  if (isInExpressionEnum(condition)) {
    return parseInExpressionEnum(condition)
  }

  // Try parsing as InExpression (search pattern)
  if (isInExpressionSearch(condition)) {
    return parseInExpressionSearch(condition)
  }

  if (isLessThanExpression(condition)) {
    return parseLessThanExpression(condition)
  }

  if (isGreaterThanExpression(condition)) {
    return parseGreaterThanExpression(condition)
  }

  if (isGreaterThanOrEqualExpression(condition)) {
    return parseGreaterThanOrEqualExpression(condition)
  }

  if (isLessThanOrEqualExpression(condition)) {
    return parseLessThanOrEqualExpression(condition)
  }

  if (isEqualityExpression(condition)) {
    return parseEqualityExpression(condition)
  }

  if (isStrictEqualityExpression(condition)) {
    return parseEqualityExpression(condition)
  }

  // Try parsing as BetweenExpression
  if (isBetweenExpression(condition)) {
    return parseBetweenExpression(condition)
  }

  return {
    success: false,
    error: {
      type: 'INVALID_STRUCTURE',
      message: 'Unknown condition pattern',
      context: { condition },
    },
  }
}

// ============================================================================
// Main Function
// ============================================================================

/**
 * Converts saved jsonLogic filters back to OneDataCollection FiltersState format.
 * This is the reverse of datatableFiltersToJsonLogicFilters.
 *
 * @param jsonLogicFilters - JSON string containing jsonLogic filter structure
 * @returns FiltersState object that can be used by OneDataCollection
 */
export function jsonLogicFiltersToFiltersState(
  jsonLogicFilters: string
): FiltersState<FiltersDefinition<string>> {
  if (Object.keys(jsonLogicFilters).length === 0) {
    return {}
  }

  const conditionsResult = extractAndConditions(JSON.parse(jsonLogicFilters))
  if (!conditionsResult.success) {
    throw new Error(`Unexpected jsonLogic structure:': ${conditionsResult.error}`)
  }

  const parseResults = conditionsResult.data
    .map((condition) => parseCondition(condition))
    .filter(isSuccessResult)

  const filtersState: Record<string, string | unknown[] | DateRange | Date | undefined> = {}
  parseResults.forEach((result) => {
    const value = result.filter.value
    if (
      typeof value === 'string' ||
      Array.isArray(value) ||
      value instanceof Date ||
      value === undefined ||
      isDateRange(value)
    ) {
      filtersState[result.filter.fieldName] = value
    }
  })

  return filtersState
}
